import React, { useState, useEffect } from 'react';
import { Button, Form, Table, Badge, Alert, Tabs, Tab, Modal } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';

function CardioLogger({ exercise, onUpdate }) {
  const isBasicCardio = exercise.exercise_type === 'cardio_basic';
  const isSegmentedCardio = exercise.exercise_type === 'cardio_segments';

  // Basic cardio state - ensure all values are never null for controlled inputs
  const [cardioData, setCardioData] = useState(() => {
    const data = exercise.cardio_data || {};
    return {
      distance: data.distance ?? '',
      distance_unit: data.distance_unit || 'miles',
      duration_seconds: data.duration_seconds ?? '',
      heart_rate_avg: data.heart_rate_avg ?? '',
      heart_rate_max: data.heart_rate_max ?? '',
      notes: data.notes || ''
    };
  });

  // Segmented cardio state
  const [segments, setSegments] = useState(exercise.cardio_segments || []);

  // History
  const [exerciseHistory, setExerciseHistory] = useState([]); // Changed to array
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);

  useEffect(() => {
    fetchExerciseHistory();
  }, [exercise.exercise_id]);

  useEffect(() => {
    // Update parent component when data changes
    if (isBasicCardio) {
      onUpdate({ cardio_data: cardioData });
    } else {
      onUpdate({ cardio_segments: segments });
    }
  }, [cardioData, segments]);

  const fetchExerciseHistory = async () => {
    setLoadingHistory(true);
    try {
      const history = await workoutAPI.fetchExerciseHistory(exercise.exercise_id);
      if (history && history.length > 0) {
        setExerciseHistory(history.slice(0, 3)); // Last 3 sessions
      }
    } catch (err) {
      console.error('Error fetching exercise history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCardioDataChange = (field, value) => {
    setCardioData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper functions to convert between seconds and HH:MM:SS
  const secondsToHMS = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { hours, minutes, seconds };
  };

  const hmsToSeconds = (hours, minutes, seconds) => {
    return (parseInt(hours) || 0) * 3600 + (parseInt(minutes) || 0) * 60 + (parseInt(seconds) || 0);
  };

  const handleDurationChange = (unit, value) => {
    const currentHMS = secondsToHMS(cardioData.duration_seconds || 0);

    let newHMS = { ...currentHMS };
    if (unit === 'hours') newHMS.hours = parseInt(value) || 0;
    if (unit === 'minutes') newHMS.minutes = parseInt(value) || 0;
    if (unit === 'seconds') newHMS.seconds = parseInt(value) || 0;

    const totalSeconds = hmsToSeconds(newHMS.hours, newHMS.minutes, newHMS.seconds);
    handleCardioDataChange('duration_seconds', totalSeconds);
  };

  const handleSegmentDurationChange = (index, unit, value) => {
    const segment = segments[index];
    const currentHMS = secondsToHMS(segment.duration_seconds || 0);

    let newHMS = { ...currentHMS };
    if (unit === 'hours') newHMS.hours = parseInt(value) || 0;
    if (unit === 'minutes') newHMS.minutes = parseInt(value) || 0;
    if (unit === 'seconds') newHMS.seconds = parseInt(value) || 0;

    const totalSeconds = hmsToSeconds(newHMS.hours, newHMS.minutes, newHMS.seconds);
    handleSegmentChange(index, 'duration_seconds', totalSeconds);
  };

  const handleAddSegment = () => {
    const newSegment = {
      segment_number: segments.length + 1,
      distance: '',
      distance_unit: 'miles',
      duration_seconds: '',
      heart_rate_avg: '',
      heart_rate_max: '',
      notes: ''
    };
    setSegments([...segments, newSegment]);
  };

  const handleRemoveSegment = (index) => {
    const confirmed = window.confirm('Delete this segment?');
    if (!confirmed) return;

    const newSegments = segments.filter((_, i) => i !== index);
    // Renumber segments
    const renumberedSegments = newSegments.map((seg, i) => ({
      ...seg,
      segment_number: i + 1
    }));
    setSegments(renumberedSegments);
  };

  const handleSegmentChange = (index, field, value) => {
    const newSegments = segments.map((seg, i) => {
      if (i === index) {
        return { ...seg, [field]: value };
      }
      return seg;
    });
    setSegments(newSegments);
  };

  const handleCopyFromLast = () => {
    if (!exerciseHistory || exerciseHistory.length === 0) return;

    const mostRecent = exerciseHistory[0];

    if (isBasicCardio && mostRecent.cardio) {
      setCardioData({
        distance: mostRecent.cardio.distance || '',
        distance_unit: mostRecent.cardio.distance_unit || 'miles',
        duration_seconds: mostRecent.cardio.duration_seconds || '',
        heart_rate_avg: mostRecent.cardio.heart_rate_avg || '',
        heart_rate_max: mostRecent.cardio.heart_rate_max || '',
        notes: ''
      });
    } else if (isSegmentedCardio && mostRecent.segments) {
      const copiedSegments = mostRecent.segments.map((seg, index) => ({
        segment_number: index + 1,
        distance: seg.distance || '',
        distance_unit: seg.distance_unit || 'miles',
        duration_seconds: seg.duration_seconds || '',
        heart_rate_avg: seg.heart_rate_avg || '',
        heart_rate_max: seg.heart_rate_max || '',
        notes: ''
      }));
      setSegments(copiedSegments);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSessionPerformance = (session) => {
    if (!session) {
      return 'No previous data';
    }

    if (isBasicCardio && session.cardio) {
      const data = session.cardio;
      const distanceStr = data.distance ? `${data.distance} ${data.distance_unit}` : 'No distance';
      const durationStr = formatDuration(data.duration_seconds);
      const hrStr = data.heart_rate_avg ? ` @ ${data.heart_rate_avg} bpm avg` : '';
      return `${distanceStr} in ${durationStr}${hrStr}`;
    } else if (isSegmentedCardio && session.segments) {
      const totalDistance = session.segments.reduce((sum, seg) => sum + (parseFloat(seg.distance) || 0), 0);
      const totalDuration = session.segments.reduce((sum, seg) => sum + (parseInt(seg.duration_seconds) || 0), 0);
      const unit = session.segments[0]?.distance_unit || 'miles';
      return `${session.segments.length} segment${session.segments.length === 1 ? '' : 's'}: ${totalDistance.toFixed(2)} ${unit} in ${formatDuration(totalDuration)}`;
    }

    return 'No previous data';
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';

    const now = new Date();
    const past = new Date(date);
    const diffDays = Math.floor((now - past) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="cardio-logger">
      {/* Last Performance Icon Button */}
      {!loadingHistory && exerciseHistory && exerciseHistory.length > 0 && (
        <div className="mb-2">
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowPerformanceModal(true)}
            className="p-0 text-info"
            title="View last performance"
          >
            <i className="fas fa-info-circle me-1"></i>
            Last performance
          </Button>
        </div>
      )}

      {/* Basic Cardio Form */}
      {isBasicCardio && (
        <div className="basic-cardio-form">
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Distance</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={cardioData.distance}
                    onChange={(e) => handleCardioDataChange('distance', e.target.value)}
                  />
                  <Form.Select
                    value={cardioData.distance_unit}
                    onChange={(e) => handleCardioDataChange('distance_unit', e.target.value)}
                    style={{ maxWidth: '100px' }}
                  >
                    <option value="miles">miles</option>
                    <option value="km">km</option>
                    <option value="meters">meters</option>
                    <option value="yards">yards</option>
                  </Form.Select>
                </div>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Duration *</Form.Label>
                <div className="d-flex gap-2">
                  <div style={{ flex: 1 }}>
                    <Form.Control
                      type="number"
                      min="0"
                      placeholder="HH"
                      value={secondsToHMS(cardioData.duration_seconds || 0).hours || ''}
                      onChange={(e) => handleDurationChange('hours', e.target.value)}
                    />
                    <Form.Text className="text-muted d-block text-center">Hours</Form.Text>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Control
                      type="number"
                      min="0"
                      max="59"
                      placeholder="MM"
                      value={secondsToHMS(cardioData.duration_seconds || 0).minutes || ''}
                      onChange={(e) => handleDurationChange('minutes', e.target.value)}
                    />
                    <Form.Text className="text-muted d-block text-center">Min</Form.Text>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Control
                      type="number"
                      min="0"
                      max="59"
                      placeholder="SS"
                      value={secondsToHMS(cardioData.duration_seconds || 0).seconds || ''}
                      onChange={(e) => handleDurationChange('seconds', e.target.value)}
                    />
                    <Form.Text className="text-muted d-block text-center">Sec</Form.Text>
                  </div>
                </div>
                {cardioData.duration_seconds > 0 && (
                  <Form.Text className="text-muted">
                    Total: {formatDuration(cardioData.duration_seconds)}
                  </Form.Text>
                )}
              </Form.Group>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Avg Heart Rate (bpm)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Optional"
                  value={cardioData.heart_rate_avg}
                  onChange={(e) => handleCardioDataChange('heart_rate_avg', e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Max Heart Rate (bpm)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Optional"
                  value={cardioData.heart_rate_max}
                  onChange={(e) => handleCardioDataChange('heart_rate_max', e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
        </div>
      )}

      {/* Segmented Cardio Form */}
      {isSegmentedCardio && (
        <div className="segmented-cardio-form">
          {segments.length > 0 ? (
            <div className="table-responsive mb-3">
              <Table striped bordered size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>Segment</th>
                    <th>Distance</th>
                    <th style={{ width: '180px' }}>Duration (H:M:S)</th>
                    <th>Avg HR</th>
                    <th>Notes</th>
                    <th style={{ width: '80px' }}>Actions</th>
                  </tr>
                </thead>
              <tbody>
                {segments.map((segment, index) => (
                  <tr key={index}>
                    <td className="text-center">
                      <Badge bg="success">{segment.segment_number}</Badge>
                    </td>
                    <td>
                      <div className="input-group input-group-sm">
                        <Form.Control
                          type="number"
                          size="sm"
                          step="0.01"
                          placeholder="0.00"
                          value={segment.distance}
                          onChange={(e) => handleSegmentChange(index, 'distance', e.target.value)}
                        />
                        <Form.Select
                          size="sm"
                          value={segment.distance_unit}
                          onChange={(e) => handleSegmentChange(index, 'distance_unit', e.target.value)}
                          style={{ maxWidth: '80px' }}
                        >
                          <option value="miles">mi</option>
                          <option value="km">km</option>
                          <option value="meters">m</option>
                          <option value="yards">yd</option>
                        </Form.Select>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          placeholder="H"
                          value={secondsToHMS(segment.duration_seconds || 0).hours || ''}
                          onChange={(e) => handleSegmentDurationChange(index, 'hours', e.target.value)}
                          style={{ width: '50px' }}
                        />
                        <span className="align-self-center">:</span>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          max="59"
                          placeholder="M"
                          value={secondsToHMS(segment.duration_seconds || 0).minutes || ''}
                          onChange={(e) => handleSegmentDurationChange(index, 'minutes', e.target.value)}
                          style={{ width: '50px' }}
                        />
                        <span className="align-self-center">:</span>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          max="59"
                          placeholder="S"
                          value={secondsToHMS(segment.duration_seconds || 0).seconds || ''}
                          onChange={(e) => handleSegmentDurationChange(index, 'seconds', e.target.value)}
                          style={{ width: '50px' }}
                        />
                      </div>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        placeholder="-"
                        value={segment.heart_rate_avg}
                        onChange={(e) => handleSegmentChange(index, 'heart_rate_avg', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Sprint, etc."
                        value={segment.notes}
                        onChange={(e) => handleSegmentChange(index, 'notes', e.target.value)}
                      />
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveSegment(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-3 mb-3 border rounded bg-light">
              <p className="text-muted mb-0">No segments added yet</p>
            </div>
          )}

          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleAddSegment}
            className="w-100"
          >
            <i className="fas fa-plus me-1"></i> Add Segment
          </Button>
        </div>
      )}

      {/* Performance Modal */}
      <Modal show={showPerformanceModal} onHide={() => setShowPerformanceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Last {exerciseHistory.length} Performance{exerciseHistory.length > 1 ? 's' : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {exerciseHistory && exerciseHistory.length > 0 ? (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {exerciseHistory.map((session, index) => (
                <div
                  key={session.id || index}
                  className="mb-3 p-3 border rounded"
                  style={{ backgroundColor: index === 0 ? '#f8f9fa' : '#ffffff' }}
                >
                  {/* Session Badge and Date */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Badge bg={index === 0 ? 'primary' : 'secondary'}>
                      {index === 0 ? 'Most Recent' : `${index + 1} session${index === 1 ? '' : 's'} ago`}
                    </Badge>
                    <small className="text-muted">
                      {new Date(session.workout_date).toLocaleDateString()} ({formatTimeAgo(session.workout_date)})
                    </small>
                  </div>

                  {/* Performance Data */}
                  <div>
                    <strong>{formatSessionPerformance(session)}</strong>
                  </div>

                  {/* Session Notes */}
                  {session.notes && (
                    <div className="mt-2 pt-2 border-top">
                      <small className="text-muted d-block">Notes:</small>
                      <div className="text-muted" style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                        {session.notes}
                      </div>
                    </div>
                  )}

                  {/* Workout Name */}
                  {session.workout_name && (
                    <div className="mt-1">
                      <small className="text-muted">
                        <i className="fas fa-dumbbell me-1"></i>
                        {session.workout_name}
                      </small>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted mb-0">No previous performance data</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowPerformanceModal(false)}>
            Close
          </Button>
          {exerciseHistory && exerciseHistory.length > 0 && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                handleCopyFromLast();
                setShowPerformanceModal(false);
              }}
            >
              <i className="fas fa-copy me-1"></i> Copy Most Recent
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CardioLogger;
